/**
 * Compact, read-only rendering of a session's declared situation — the strip
 * every participant sees. Renders nothing when nothing was declared, so a
 * session that skipped the questions looks exactly as it did before.
 */

import {
  SITUATION_DIMENSIONS,
  SITUATION_QUESTIONS,
  isEmptySituationVector,
  situationValueLabel,
  type SituationVector,
} from '../../../types/prioritization/situationVector';

interface Props {
  vector: SituationVector | null | undefined;
  className?: string;
}

export function SituationReadout({ vector, className = '' }: Props) {
  if (isEmptySituationVector(vector)) return null;
  const declared = SITUATION_DIMENSIONS.filter((d) => vector?.[d]);
  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 text-xs ${className}`}
      aria-label="Declared situation"
      data-testid="situation-readout"
    >
      <span className="font-semibold uppercase tracking-wide text-[var(--text-ter)]">The room</span>
      {declared.map((d) => {
        const question = SITUATION_QUESTIONS.find((q) => q.dimension === d);
        return (
          <span
            key={d}
            title={question?.question}
            className="inline-flex items-center gap-1 border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-0.5 text-[var(--text-sec)]"
          >
            <span className="text-[var(--text-ter)]">{question?.title ?? d}:</span>
            <span className="font-medium text-[var(--text-pri)]">{situationValueLabel(d, vector?.[d])}</span>
          </span>
        );
      })}
    </div>
  );
}
